import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import Button from "@/components/ui/button";
import IconLinkChain from "@/components/icons/icon-link-chain";
import { copyToClipboard } from "@/lib/utils";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";
import { useAuth } from "@/modules/auth";

interface FormPublishedLinkModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	formTitle: string;
	link: string;
}

export default function FormPublishedLinkModal({ open, onOpenChange, formTitle, link }: FormPublishedLinkModalProps) {
	const { currentWorkspaceId } = useAuth();
	const [copied, setCopied] = useState(false);

	const handleCopyLink = async () => {
		try {
			await copyToClipboard(link);
			if (currentWorkspaceId) useOnboardingStore.getState().completeStep(currentWorkspaceId, "share_form");
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error("Failed to copy:", err);
		}
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-md p-6 modal-content">
					{/* Success Icon */}
					<div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-green-100">
						<svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
						</svg>
					</div>

					{/* Title */}
					<Dialog.Title className="text-lg font-semibold text-gray-900 text-center mb-2">Your form is live!</Dialog.Title>

					{/* Description */}
					<Dialog.Description className="text-sm text-tertiary text-center mb-6">
						<span className="font-medium text-gray-900">"{formTitle}"</span> has been published successfully. <br />
						Share this link to start collecting testimonials.
					</Dialog.Description>

					{/* Link Display Section */}
					<div className="mb-6">
						<div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
							<div className="flex-1 min-w-0">
								<p className="text-sm text-gray-600 truncate">{link}</p>
							</div>
							<Button variant="light" className="flex items-center justify-center gap-2 w-24 shrink-0" onClick={handleCopyLink} type="button">
								{copied ? (
									<>
										<svg className="w-4 h-4 shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
										</svg>
										<span className="text-green-600">Copied!</span>
									</>
								) : (
									<>
										<IconLinkChain className="shrink-0" />
										<span>Copy</span>
									</>
								)}
							</Button>
						</div>
					</div>

					{/* Actions */}
					<div className="flex gap-3">
						<Button variant="default" className="flex-1" onClick={() => onOpenChange(false)}>
							Done
						</Button>
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
